import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import React, { useCallback } from 'react';
import { Subscribe } from 'unstated';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ErrorPageComponent } from '@confluence/not-found/entry-points/ErrorPageComponent.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { RESTRICTED_SPACE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useAccessRequestSettings } from '@confluence/access-requests/entry-points/useAccessRequestSettings.ts';
import { AccessDisabledMessage } from '@confluence/access-requests/entry-points/AccessDisabledMessage.ts';

import CantViewLightModeMohiti from './CantViewLightMohiti.svg';
import CantViewDarkModeMohiti from './CantViewDarkMohiti.svg';
import ConfluenceLogo from './ConfluenceLogo.svg';
import RestrictedContentIconDark from './RestrictedContentIcon-Dark.svg';
import RestrictedContentIcon from './RestrictedContentIcon-Light.svg';
import { INACCESSIBLE_PAGE_LOAD } from './perf.config.js';
import { ShowSpinnerAfterDelay } from './ShowSpinnerAfterDelay.tsx';
import { SuperAdminGateway } from './SuperAdminGateway.js';
import { useButtonProps } from './useRestrictedSpaceScreenButtonProps.tsx';
import { useRequestSpaceAccessMutation } from './useRequestSpaceAccessMutation.tsx';
import type { ConfluenceJoinabilityType } from './request-license/useIsAuthenticatedUnlicensedWithDirectAccess.ts';

const i18n = defineMessages({
	accessDisabledTitle: {
		id: 'no-permission.restricted.space.access-requests-disabled.title.non-final',
		defaultMessage: 'This content is locked for now',
		description:
			'Title shown on the restricted space screen when space access requests are disabled by admin',
	},
	accessDisabledMessage: {
		id: 'no-permission.restricted.space.access-requests-disabled.message.non-final',
		defaultMessage: 'Only some people can view this content.',
		description:
			'Default message shown on the restricted space screen when space access requests are disabled and no custom message is configured',
	},
});

const PAGE_LOAD_CUSTOM_DATA = {
	inaccessiblePageType: 'RESTRICTED_SPACE',
};

const FLAGS_CONTAINERS = [FlagsStateContainer];

interface RestrictedSpaceScreenProps {
	contentId?: string;
	confluenceJoinability: ConfluenceJoinabilityType;
	identityLink: string;
}

export const RestrictedSpaceForLicensedUserComponent = ({
	flags,
	contentId,
	confluenceJoinability,
	identityLink,
}: WithFlagsProps & RestrictedSpaceScreenProps): React.JSX.Element => {
	const spaceKey = useSpaceKey() || '';
	const { formatMessage } = useIntl();
	const { isLoggedIn } = useSessionData();

	// Skipping the fetch gates the whole feature — the copy below and the buttons in useButtonProps
	// both branch on `spaceEnabled`, which stays undefined while skipped. Anonymous users are
	// skipped to match preloadAccessRequestSettings; useButtonProps offers them only "Go home"
	// regardless. UNSAFE_expValEqualsNoExposure so exposures come from the admin settings page
	// rather than every restricted-space view.
	const {
		spaceEnabled,
		spaceMessageContent,
		loading: isAccessRequestSettingsLoading,
	} = useAccessRequestSettings({
		skip:
			!isLoggedIn ||
			!UNSAFE_expValEqualsNoExposure('cc_perms_ga_access_requests', 'isEnabled', true),
	});

	const { isRequestAccessPending, updateSpaceAccessMutation } = useRequestSpaceAccessMutation({
		flags,
	});

	const { primary, secondary } = useButtonProps({
		confluenceJoinability,
		identityLink,
		updateSpaceAccessMutation,
		spaceKey,
		contentId,
		isRequestAccessPending,
		spaceEnabled,
	});

	const getErrorMessages = useCallback(() => {
		switch (confluenceJoinability) {
			case 'canJoinDirectly':
				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.title.authenticated.unlicensed"
							description="Title displayed for a restricted space for an authenticated user without a license"
							defaultMessage="You need full Confluence access to view this space"
						/>
					),
					errorMessage: (
						<FormattedMessage
							id="no-permission.restricted.space.message.authenticated.unlicensed"
							description="Message displayed for restricted space for an authenticated user without a license"
							// eslint-disable-next-line @atlassian/i18n/no-multiple-whitespaces
							defaultMessage="Your access to Confluence is currently limited. To view this space, you’ll need submit a request for full access.
			  If this space is restricted, you may also need to submit request for space access."
						/>
					),
					errorImage: RestrictedContentIcon,
					errorImageDark: RestrictedContentIconDark,
					layout: 'default' as const,
				};
			case 'licensingInProgress':
				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.title.authenticated.licensing.in.progress"
							description="Title displayed for a restricted space for an authenticated user with licensing in progress"
							defaultMessage="Your Confluence access is still processing"
						/>
					),
					errorMessage: null,
					errorImage: ConfluenceLogo,
					errorImageDark: ConfluenceLogo,
					layout: 'default' as const,
				};
			default:
				// Strictly `=== false`, not `!spaceEnabled`: undefined means loading, skipped, or
				// failed, and those must keep the request-access screen.
				if (spaceEnabled === false) {
					return {
						errorTitle: formatMessage(i18n.accessDisabledTitle),
						errorMessage: (
							<AccessDisabledMessage
								messageContent={spaceMessageContent}
								fallback={formatMessage(i18n.accessDisabledMessage)}
							/>
						),
						errorImage: CantViewLightModeMohiti,
						errorImageDark: CantViewDarkModeMohiti,
						layout: 'centered' as const,
					};
				}

				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.licensed-user.request-access.title"
							defaultMessage="You don’t have access to this space"
							description="title text for the restricted space screen for licensed users"
						/>
					),
					errorMessage: null,
					errorImage: RestrictedContentIcon,
					errorImageDark: RestrictedContentIconDark,
					layout: 'default' as const,
				};
		}
	}, [confluenceJoinability, spaceEnabled, spaceMessageContent, formatMessage]);

	// Hold the screen until the setting is known, as RestrictedSpace does for isDomainAccessLoading —
	// rendering early commits to the request-access screen and then visibly swaps. Preloading covers
	// SSR, but SPA transitions have none. The joinability screens don't read the setting, so they
	// must not be delayed. No gate check needed: a skipped query reports loading: false.
	const isJoinabilityScreen =
		confluenceJoinability === 'canJoinDirectly' || confluenceJoinability === 'licensingInProgress';

	if (isAccessRequestSettingsLoading && !isJoinabilityScreen) {
		return <ShowSpinnerAfterDelay />;
	}

	const { errorTitle, errorMessage, errorImage, errorImageDark, layout } = getErrorMessages();

	return (
		<>
			<ErrorPageComponent
				errorImage={errorImage}
				errorImageDark={errorImageDark}
				errorTitle={errorTitle}
				errorMessage={errorMessage}
				primaryButtonProps={primary}
				secondaryButtonProps={secondary}
				layout={layout}
			>
				<SuperAdminGateway type="space" contentId={contentId} />
			</ErrorPageComponent>

			<ExperienceSuccess name={RESTRICTED_SPACE_EXPERIENCE} />
			<PageLoadEnd metric={INACCESSIBLE_PAGE_LOAD} customData={PAGE_LOAD_CUSTOM_DATA} />
		</>
	);
};

export const RestrictedSpaceScreen = (props: RestrictedSpaceScreenProps): React.JSX.Element => (
	<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
		<Subscribe to={FLAGS_CONTAINERS}>
			{(flags: FlagsStateContainer) => (
				<RestrictedSpaceForLicensedUserComponent flags={flags} {...props} />
			)}
		</Subscribe>
	</ErrorBoundary>
);
